import React, { FormEvent, useEffect, useMemo, useState } from "react";
import {
  ClientValue,
  CreateHomeworkActivityFields,
  PortalActivityTemplateDetail,
  PortalActivityTemplateSummary,
  PortalActivityType,
  PortalPatient,
  Weekday,
  weekdays,
} from "../api/contracts";
import { ClinicianPortalApiError } from "../api/clinicianPortalApi";
import {
  getActivityTypePillToken,
  getHomeworkRepeatPillToken,
  getWeekdayPillToken,
  PillPaletteToken,
} from "../config/palette";
import {
  ActivityTypePill,
  ContentSection,
  RecordDetailCard,
  RecordFieldRow,
} from "./DataDisplay";
import { MultiSelectPillField } from "./MultiSelectPillField";
import { HomeworkActivityFieldHelp } from "./HomeworkActivityFieldHelp";
import { PortalLink } from "./PortalLink";
import { SingleSelectPillField } from "./SingleSelectPillField";

type RepeatChoice = "" | "Daily" | "Weekly";

export interface CreateHomeworkActivityFormValues {
  patientId: string;
  templateId: string;
  homeworkName: string;
  activityTypeId: string;
  valueSupportedId: string;
  nonExposureHomework: boolean;
  activitySteps: string;
  alternative: string;
  coachingPrompts: string;
  points: string;
  coins: string;
  dueDate: string;
  routineTime: string;
  repeats: RepeatChoice;
  repeatOn: Weekday[];
  recurrenceEndDate: string;
}

type CreateHomeworkActivityFormErrors = Partial<
  Record<
    | "patientId"
    | "templateId"
    | "activityTypeId"
    | "points"
    | "coins"
    | "dueDate"
    | "repeats"
    | "repeatOn"
    | "recurrenceEndDate",
    string
  >
>;

export const emptyCreateHomeworkActivityFormValues: CreateHomeworkActivityFormValues =
  {
    patientId: "",
    templateId: "",
    homeworkName: "",
    activityTypeId: "",
    valueSupportedId: "",
    nonExposureHomework: false,
    activitySteps: "",
    alternative: "",
    coachingPrompts: "",
    points: "0",
    coins: "0",
    dueDate: "",
    routineTime: "",
    repeats: "",
    repeatOn: [],
    recurrenceEndDate: "",
  };

export function validateCreateHomeworkActivityForm(
  values: CreateHomeworkActivityFormValues,
): CreateHomeworkActivityFormErrors {
  const errors: CreateHomeworkActivityFormErrors = {};
  if (!values.patientId) errors.patientId = "Client is required.";
  if (!values.templateId || !values.homeworkName.trim()) {
    errors.templateId = "Activity Template is required.";
  }
  if (!values.activityTypeId) {
    errors.activityTypeId = "Activity Type is required.";
  }
  if (values.points.trim() === "" || !Number.isInteger(Number(values.points))) {
    errors.points = "Activity Points must be an integer.";
  }
  if (values.coins.trim() === "" || !Number.isInteger(Number(values.coins))) {
    errors.coins = "Activity Coins must be an integer.";
  }
  if (
    values.repeats !== "" &&
    values.repeats !== "Daily" &&
    values.repeats !== "Weekly"
  ) {
    errors.repeats = "Select a valid recurrence.";
  }
  if (values.repeats && !values.dueDate) {
    errors.dueDate = "Due Date is required for repeating homework.";
  }
  if (values.repeats === "Weekly" && !values.repeatOn.length) {
    errors.repeatOn = "Select at least one weekday for a weekly recurrence.";
  }
  const recurrenceEndDate = values.recurrenceEndDate || values.dueDate;
  if (
    values.repeats &&
    values.dueDate &&
    recurrenceEndDate &&
    recurrenceEndDate < values.dueDate
  ) {
    errors.recurrenceEndDate =
      "Recurrence End Date must be on or after Due Date.";
  }
  return errors;
}

export function buildCreateHomeworkActivityFields(
  values: CreateHomeworkActivityFormValues,
): CreateHomeworkActivityFields {
  const isRecurring = values.repeats !== "";
  return {
    templateId: values.templateId,
    homeworkName: values.homeworkName.trim(),
    activityTypeId: values.activityTypeId,
    valueSupportedId: values.valueSupportedId,
    nonExposureHomework: values.nonExposureHomework,
    activitySteps: values.activitySteps,
    alternative: values.alternative,
    coachingPrompts: values.coachingPrompts,
    points: Number(values.points),
    coins: Number(values.coins),
    dueDate: values.dueDate,
    routineTime: values.routineTime,
    isRecurring,
    repeats: isRecurring ? values.repeats : "",
    repeatOn: values.repeats === "Weekly" ? values.repeatOn : [],
    recurrenceEndDate: isRecurring
      ? values.recurrenceEndDate || values.dueDate
      : "",
  };
}

const neutral = (): PillPaletteToken => "grayLight2";

function submissionErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (!(error instanceof ClinicianPortalApiError)) {
    return "The Homework Activity could not be created. Please try again.";
  }
  if (error.code === "NOT_AUTHORIZED" || error.code === "NOT_FOUND") {
    return "Homework Activity creation is unavailable.";
  }
  if (error.code === "INVALID_REQUEST") {
    return error.message || "Review the form values and try again.";
  }
  return "The Homework Activity could not be created. Please try again.";
}

export function CreateHomeworkActivityForm({
  clinicianCode,
  clients,
  activityTypes,
  templates,
  templatesLoading,
  templatesError,
  retryTemplates,
  selectedTemplate,
  selectedTemplateLoading,
  selectedTemplateError,
  retrySelectedTemplate,
  clientValues,
  clientValuesLoading,
  clientValuesError,
  retryClientValues,
  cancelPath,
  isSubmitting,
  submissionError,
  clearSubmissionError,
  onPatientChange,
  onTemplateChange,
  onSubmit,
}: {
  clinicianCode: string;
  clients: readonly PortalPatient[];
  activityTypes: readonly PortalActivityType[];
  templates: readonly PortalActivityTemplateSummary[];
  templatesLoading: boolean;
  templatesError?: unknown;
  retryTemplates: () => void;
  selectedTemplate: PortalActivityTemplateDetail | null;
  selectedTemplateLoading: boolean;
  selectedTemplateError?: unknown;
  retrySelectedTemplate: () => void;
  clientValues: readonly ClientValue[];
  clientValuesLoading: boolean;
  clientValuesError?: unknown;
  retryClientValues: () => void;
  cancelPath: string;
  isSubmitting: boolean;
  submissionError?: unknown;
  clearSubmissionError: () => void;
  onPatientChange: (patientId: string) => void;
  onTemplateChange: (templateId: string) => void;
  onSubmit: (
    patientId: string,
    fields: CreateHomeworkActivityFields,
  ) => Promise<void>;
}) {
  const [values, setValues] = useState(emptyCreateHomeworkActivityFormValues);
  const [errors, setErrors] = useState<CreateHomeworkActivityFormErrors>({});
  const clientOptions = useMemo(
    () =>
      clients.map((client) => ({
        id: client.id,
        value: client.id,
        label: `${client.accessCode} • ${client.nickname}`,
      })),
    [clients],
  );
  const activityTypeColorByName = useMemo(
    () =>
      new Map(
        activityTypes.map((activityType) => [
          activityType.name,
          activityType.colorKey,
        ]),
      ),
    [activityTypes],
  );
  const templateActivityTypeById = useMemo(
    () =>
      new Map(
        templates.map((template) => [template.id, template.activityType]),
      ),
    [templates],
  );
  const templateOptions = useMemo(
    () =>
      templates.map((template) => ({
        id: template.id,
        value: template.id,
        label: template.name,
        colorKey: activityTypeColorByName.get(template.activityType),
      })),
    [activityTypeColorByName, templates],
  );
  const valueOptions = useMemo(
    () =>
      clientValues
        .filter(
          (value) => value.enabled && value.patientId === values.patientId,
        )
        .map((value) => ({
          id: value.id,
          value: value.id,
          label: value.valueName,
        })),
    [clientValues, values.patientId],
  );
  const apiError = submissionErrorMessage(submissionError);
  const selectedActivityType = activityTypes.find(
    (activityType) => activityType.id === values.activityTypeId,
  );

  useEffect(() => {
    if (!selectedTemplate) return;
    setValues((current) =>
      current.templateId === selectedTemplate.id
        ? {
            ...current,
            homeworkName: selectedTemplate.name,
            activityTypeId: selectedTemplate.activityTypeId,
            nonExposureHomework: selectedTemplate.nonExposureHomework,
            activitySteps: selectedTemplate.activitySteps,
            alternative: selectedTemplate.alternative,
            coachingPrompts: selectedTemplate.coachingPrompts,
            points: String(selectedTemplate.points),
            coins: String(selectedTemplate.coins),
          }
        : current,
    );
  }, [selectedTemplate]);

  const clearErrors = (field?: keyof CreateHomeworkActivityFormErrors) => {
    setErrors((current) => {
      if (!field) return {};
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
    if (!isSubmitting) clearSubmissionError();
  };
  const set = <K extends keyof CreateHomeworkActivityFormValues>(
    key: K,
    value: CreateHomeworkActivityFormValues[K],
  ) => {
    clearErrors(key as keyof CreateHomeworkActivityFormErrors);
    setValues((current) => ({ ...current, [key]: value }));
  };
  const handlePatientChange = (patientId: string) => {
    clearErrors("patientId");
    setValues((current) => ({
      ...current,
      patientId,
      valueSupportedId: "",
    }));
    onPatientChange(patientId);
  };
  const handleTemplateChange = (templateId: string) => {
    if (templateId === values.templateId) return;
    clearErrors("templateId");
    clearErrors("activityTypeId");
    const template = templates.find((option) => option.id === templateId);
    setValues((current) => ({
      ...current,
      templateId,
      homeworkName: template?.name ?? "",
      activityTypeId: "",
      nonExposureHomework: false,
      activitySteps: "",
      alternative: "",
      coachingPrompts: "",
      points: "0",
      coins: "0",
    }));
    onTemplateChange(templateId);
  };
  const handleDueDate = (dueDate: string) => {
    clearErrors("dueDate");
    if (!dueDate) {
      clearErrors("repeats");
      clearErrors("repeatOn");
      clearErrors("recurrenceEndDate");
    }
    setValues((current) => {
      if (!dueDate)
        return {
          ...current,
          dueDate: "",
          repeats: "",
          repeatOn: [],
          recurrenceEndDate: "",
        };
      const followsDueDate = Boolean(
        current.dueDate && current.recurrenceEndDate === current.dueDate,
      );
      return {
        ...current,
        dueDate,
        recurrenceEndDate:
          current.repeats && (!current.recurrenceEndDate || followsDueDate)
            ? dueDate
            : current.recurrenceEndDate,
      };
    });
  };
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting || selectedTemplateLoading) return;
    const nextErrors = validateCreateHomeworkActivityForm(values);
    setErrors(nextErrors);
    clearSubmissionError();
    if (Object.keys(nextErrors).length) return;
    await onSubmit(values.patientId, buildCreateHomeworkActivityFields(values));
  };
  const input = (
    key: keyof CreateHomeworkActivityFormValues,
    label: string,
    type = "text",
  ) => (
    <div
      className={`cp-form-field cp-homework-edit-control${
        type === "date" ? " cp-homework-edit-date-control" : ""
      }`}
    >
      <label className="cp-visually-hidden" htmlFor={`create-homework-${key}`}>
        {label}
      </label>
      <input
        id={`create-homework-${key}`}
        className={`cp-form-input${
          key === "points" || key === "coins"
            ? " cp-form-input--no-spinner"
            : ""
        }`}
        type={type}
        step={type === "number" ? "1" : undefined}
        value={String(values[key])}
        disabled={isSubmitting}
        aria-invalid={Boolean(
          errors[key as keyof CreateHomeworkActivityFormErrors],
        )}
        onChange={(event) =>
          key === "dueDate"
            ? handleDueDate(event.target.value)
            : set(key as never, event.target.value as never)
        }
      />
      {errors[key as keyof CreateHomeworkActivityFormErrors] ? (
        <p className="cp-form-error">
          {errors[key as keyof CreateHomeworkActivityFormErrors]}
        </p>
      ) : null}
    </div>
  );
  const textarea = (
    key: "activitySteps" | "alternative" | "coachingPrompts",
    label: string,
  ) => (
    <div className="cp-form-field cp-homework-edit-control">
      <label className="cp-visually-hidden" htmlFor={`create-homework-${key}`}>
        {label}
      </label>
      <textarea
        id={`create-homework-${key}`}
        className="cp-form-input cp-form-textarea"
        rows={4}
        value={values[key]}
        disabled={isSubmitting}
        onChange={(event) => set(key, event.target.value)}
      />
    </div>
  );

  return (
    <form
      className="cp-homework-edit-form cp-docked-form"
      aria-label="Create Homework Activity"
      onSubmit={handleSubmit}
      noValidate
    >
      <ContentSection title="Homework Activity Details">
        <RecordDetailCard>
          <RecordFieldRow label="Client" required>
            <div className="cp-form-field cp-homework-edit-control">
              <SingleSelectPillField
                id="create-homework-client"
                value={values.patientId}
                options={clientOptions}
                onChange={handlePatientChange}
                ariaLabel="Client"
                placeholder="Select a client"
                searchPlaceholder="Find a client"
                emptyMessage="No clients found"
                resolveColor={neutral}
                disabled={isSubmitting}
                invalid={Boolean(errors.patientId)}
              />
              {errors.patientId ? (
                <p className="cp-form-error">{errors.patientId}</p>
              ) : null}
            </div>
          </RecordFieldRow>
          <RecordFieldRow label="Activity Template" required>
            <div className="cp-form-field cp-homework-edit-control">
              <SingleSelectPillField
                id="create-homework-template"
                value={values.templateId}
                options={templateOptions}
                onChange={handleTemplateChange}
                ariaLabel="Activity Template"
                placeholder={
                  templatesLoading
                    ? "Loading activity templates…"
                    : templatesError
                      ? "Activity templates unavailable"
                      : "Select Activity Template"
                }
                searchPlaceholder="Search templates"
                emptyMessage="No active templates available"
                resolveColor={(option) =>
                  getActivityTypePillToken(
                    templateActivityTypeById.get(option.value) ?? "",
                    option.colorKey,
                  )
                }
                disabled={
                  isSubmitting || templatesLoading || Boolean(templatesError)
                }
                invalid={Boolean(errors.templateId)}
              />
              {templatesLoading ? (
                <p className="cp-form-help" role="status">
                  Loading activity templates…
                </p>
              ) : null}
              {templatesError ? (
                <div className="cp-form-inline-error" role="alert">
                  <span>Activity templates could not be loaded.</span>
                  <button type="button" onClick={retryTemplates}>
                    Try again
                  </button>
                </div>
              ) : null}
              {errors.templateId ? (
                <p className="cp-form-error">{errors.templateId}</p>
              ) : null}
              {selectedTemplateLoading ? (
                <p className="cp-form-help">Loading Activity Template…</p>
              ) : null}
              {values.templateId && selectedTemplateError ? (
                <div className="cp-form-inline-error" role="alert">
                  <span>Activity Template could not be loaded.</span>
                  <button type="button" onClick={retrySelectedTemplate}>
                    Try again
                  </button>
                </div>
              ) : null}
            </div>
          </RecordFieldRow>
          <RecordFieldRow label="Activity Type">
            <div
              className="cp-homework-edit-readonly"
              aria-label="Activity Type"
            >
              {selectedTemplateLoading ? (
                <span className="cp-empty-value">Loading…</span>
              ) : selectedActivityType || selectedTemplate?.activityType ? (
                <ActivityTypePill
                  activityType={
                    selectedActivityType?.name ||
                    selectedTemplate?.activityType ||
                    ""
                  }
                  colorKey={selectedActivityType?.colorKey}
                />
              ) : (
                <span className="cp-empty-value">
                  Select an Activity Template first.
                </span>
              )}
              {errors.activityTypeId ? (
                <p className="cp-form-error">{errors.activityTypeId}</p>
              ) : null}
            </div>
          </RecordFieldRow>
          <RecordFieldRow label="Value Supported">
            <div className="cp-form-field cp-homework-edit-control">
              <SingleSelectPillField
                id="create-homework-value"
                value={values.valueSupportedId}
                options={valueOptions}
                onChange={(valueSupportedId) =>
                  set("valueSupportedId", valueSupportedId)
                }
                ariaLabel="Value Supported"
                placeholder={
                  values.patientId
                    ? "Select a client value"
                    : "Select a client first"
                }
                searchPlaceholder="Find a value"
                emptyMessage="No client values found"
                resolveColor={neutral}
                clearable
                disabled={
                  isSubmitting || !values.patientId || clientValuesLoading
                }
              />
              {clientValuesLoading ? (
                <p className="cp-form-help">Loading client values…</p>
              ) : null}
              {values.patientId && clientValuesError ? (
                <div className="cp-form-inline-error" role="alert">
                  <span>Client values could not be loaded.</span>
                  <button type="button" onClick={retryClientValues}>
                    Try again
                  </button>
                </div>
              ) : null}
            </div>
          </RecordFieldRow>
          <RecordFieldRow label="Non-Exposure Homework">
            <HomeworkActivityFieldHelp field="nonExposureHomework" />
            <label className="cp-form-checkbox cp-homework-edit-checkbox">
              <input
                type="checkbox"
                aria-label="Non-Exposure Homework"
                checked={values.nonExposureHomework}
                disabled={isSubmitting}
                onChange={(event) =>
                  set("nonExposureHomework", event.target.checked)
                }
              />
              {values.nonExposureHomework ? "Yes" : "No"}
            </label>
          </RecordFieldRow>
          <RecordFieldRow label="Due Date">
            <HomeworkActivityFieldHelp field="dueDate" />
            {input("dueDate", "Due Date", "date")}
          </RecordFieldRow>
          {values.dueDate ? (
            <RecordFieldRow label="Repeats">
              <HomeworkActivityFieldHelp field="repeats" />
              <div className="cp-form-field cp-homework-edit-control">
                <SingleSelectPillField
                  id="create-homework-repeats"
                  value={values.repeats}
                  options={[
                    {
                      id: "does-not-repeat",
                      value: "",
                      label: "Does not repeat",
                    },
                    { id: "daily", value: "Daily", label: "Daily" },
                    { id: "weekly", value: "Weekly", label: "Weekly" },
                  ]}
                  onChange={(value) => {
                    clearErrors("repeats");
                    const repeats = value as RepeatChoice;
                    setValues((current) => ({
                      ...current,
                      repeats,
                      repeatOn: repeats === "Weekly" ? current.repeatOn : [],
                      recurrenceEndDate: repeats
                        ? current.recurrenceEndDate || current.dueDate
                        : "",
                    }));
                  }}
                  ariaLabel="Repeats"
                  placeholder="Select a recurrence"
                  searchPlaceholder="Find a recurrence"
                  emptyMessage="No recurrence options found"
                  resolveColor={(option) =>
                    getHomeworkRepeatPillToken(option.value)
                  }
                  disabled={isSubmitting}
                  invalid={Boolean(errors.repeats)}
                />
                {errors.repeats ? (
                  <p className="cp-form-error">{errors.repeats}</p>
                ) : null}
              </div>
            </RecordFieldRow>
          ) : null}
          {values.dueDate && values.repeats === "Weekly" ? (
            <RecordFieldRow label="Repeat On">
              <div className="cp-form-field cp-homework-edit-control">
                <MultiSelectPillField
                  value={values.repeatOn}
                  options={weekdays.map((day) => ({
                    id: day,
                    value: day,
                    label: day,
                  }))}
                  onChange={(repeatOn) =>
                    set("repeatOn", repeatOn as Weekday[])
                  }
                  ariaLabel="Repeat On weekdays"
                  placeholder="Select weekdays"
                  searchPlaceholder="Find a weekday"
                  emptyMessage="No weekdays found"
                  resolveColor={(option) => getWeekdayPillToken(option.value)}
                  disabled={isSubmitting}
                  invalid={Boolean(errors.repeatOn)}
                />
                {errors.repeatOn ? (
                  <p className="cp-form-error">{errors.repeatOn}</p>
                ) : null}
              </div>
            </RecordFieldRow>
          ) : null}
          {values.dueDate && values.repeats ? (
            <RecordFieldRow label="Recurrence End Date">
              <HomeworkActivityFieldHelp field="recurrenceEndDate" />
              {input("recurrenceEndDate", "Recurrence End Date", "date")}
            </RecordFieldRow>
          ) : null}
          <RecordFieldRow label="Activity Points">
            {input("points", "Activity Points", "number")}
          </RecordFieldRow>
          <RecordFieldRow label="Activity Coins">
            {input("coins", "Activity Coins", "number")}
          </RecordFieldRow>
          <RecordFieldRow label="Activity Steps">
            <HomeworkActivityFieldHelp field="activitySteps" />
            {textarea("activitySteps", "Activity Steps")}
          </RecordFieldRow>
          <RecordFieldRow label="Modification Steps">
            <HomeworkActivityFieldHelp field="modificationSteps" />
            {textarea("alternative", "Modification Steps")}
          </RecordFieldRow>
          <RecordFieldRow label="Coaching Prompts">
            <HomeworkActivityFieldHelp field="coachingPrompts" />
            {textarea("coachingPrompts", "Coaching Prompts")}
          </RecordFieldRow>
          <RecordFieldRow label="Activity Images">
            <HomeworkActivityFieldHelp field="activityImages" />
            <p className="cp-form-help">
              Save this activity first to add images.
            </p>
          </RecordFieldRow>
        </RecordDetailCard>
      </ContentSection>

      <div className="cp-homework-edit-footer cp-docked-form__footer">
        {apiError ? (
          <p className="cp-form-error cp-form-error--panel" role="alert">
            {apiError}
          </p>
        ) : null}
        <div className="cp-form-actions">
          <PortalLink
            className="cp-button cp-button--secondary"
            path={cancelPath}
            clinicianCode={clinicianCode}
          >
            Cancel
          </PortalLink>
          <button
            type="submit"
            className="cp-button cp-button--primary"
            disabled={isSubmitting || selectedTemplateLoading}
          >
            {isSubmitting ? "Adding…" : "Add Activity"}
          </button>
        </div>
      </div>
    </form>
  );
}
